import React, {
  createContext,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react'
import LoadingIndicator from '@components/common/LoadingIndicator/LoadingIndicator.component'
import { resolveLoadingPortalTarget } from '@utils/resolveLoadingPortalTarget'

export type GlobalLoadingOptions = {
  container: string
  height?: number
  style?: React.CSSProperties
  hideElements?: string[]
}

export type GlobalLoadingActions = {
  showLoading: (options: GlobalLoadingOptions) => void
  hideLoading: (container: string) => void
  setLoading: (value: boolean, options?: GlobalLoadingOptions) => void
}

export type GlobalLoadingState = {
  isLoading: boolean
}

export const GlobalLoadingActionsContext =
  createContext<GlobalLoadingActions | null>(null)
export const GlobalLoadingStateContext =
  createContext<GlobalLoadingState | null>(null)

export function GlobalLoadingProvider({
  children,
}: {
  children: React.ReactNode
}): JSX.Element {
  const [isLoading, setIsLoading] = useState(false)
  const [options, setOptions] = useState<GlobalLoadingOptions | undefined>(
    undefined,
  )
  const hideElementsTimeout = useRef<NodeJS.Timeout | null>(null)

  const showLoading = useCallback((opts: GlobalLoadingOptions) => {
    if (Array.isArray(opts.hideElements) && opts.hideElements?.length > 0) {
      const waitForElements = (): void => {
        let allFound = true

        opts.hideElements?.forEach((selector) => {
          const element = document.querySelector(selector)
          if (element) {
            ;(element as HTMLElement).style.visibility = 'hidden'
          } else {
            allFound = false
          }
        })

        if (!allFound) {
          hideElementsTimeout.current = setTimeout(waitForElements, 100)
        }
      }

      waitForElements()
    }
    setOptions(opts)
    setIsLoading(true)
  }, [])

  const hideLoading = useCallback(
    (container: string) => {
      setOptions((prevOptions) => {
        if (prevOptions?.container !== container) {
          return prevOptions
        }

        if (
          Array.isArray(prevOptions?.hideElements) &&
          prevOptions.hideElements.length > 0
        ) {
          if (hideElementsTimeout.current !== null) {
            clearTimeout(hideElementsTimeout.current as NodeJS.Timeout)
            hideElementsTimeout.current = null
          }
          prevOptions.hideElements.forEach((selector) => {
            const element = document.querySelector(selector)
            if (element) {
              ;(element as HTMLElement).style.visibility = 'visible'
            }
          })
        }

        setIsLoading(false)

        return undefined
      })
    },
    [], // No need to depend on `options` anymore, since we're using the functional form
  )

  const setLoading = useCallback(
    (value: boolean, opts?: GlobalLoadingOptions) => {
      if (value) {
        setOptions(opts)
        setIsLoading(true)
      } else {
        setIsLoading(false)
        setOptions(undefined)
      }
    },
    [],
  )

  const actionsValue = useMemo(
    () => ({ showLoading, hideLoading, setLoading }),
    [showLoading, hideLoading, setLoading],
  )

  const stateValue = useMemo(() => ({ isLoading }), [isLoading])

  const loadingPortalTarget =
    isLoading &&
    resolveLoadingPortalTarget(
      options?.container
        ? (document.querySelector(options.container) as HTMLElement | null)
        : undefined,
    )

  return (
    <GlobalLoadingActionsContext.Provider value={actionsValue}>
      <GlobalLoadingStateContext.Provider value={stateValue}>
        {children}

        <LoadingIndicator
          isLoading={isLoading}
          container={loadingPortalTarget || undefined}
          height={options?.height}
          style={options?.style}
        />
      </GlobalLoadingStateContext.Provider>
    </GlobalLoadingActionsContext.Provider>
  )
}
